import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Mail, MapPin, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { ENQUIRY } from '../../constants/testIds';

const INTEREST_OPTIONS = [
  'The 605 — 605 sq yd home',
  'The 1210 — 1210 sq yd home',
  'I would like to walk the site',
  'I would like a brochure',
  'General enquiry',
];

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export const Enquiry = ({
  eyebrow = 'Begin a Conversation',
  heading,
  intro = 'Share a few details and a residence custodian will arrange a private site visit, at your pace, on the estate that calls you.',
} = {}) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', interest: INTEREST_OPTIONS[0], message: '' });
  const [submitting, setSubmitting] = useState(false);
  const { pathname } = useLocation();

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please share your name and email so we can write back.');
      return;
    }
    if (!isValidEmail(form.email.trim())) {
      toast.error('That email doesn\'t look right — could you check it once?');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/enquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          interest: form.interest || null,
          message: form.message.trim() || null,
          source: pathname || 'home',
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = Array.isArray(err.detail) ? err.detail[0]?.msg : err.detail;
        throw new Error(detail || `Request failed (${res.status})`);
      }
      const data = await res.json();
      toast.success(data.message || 'Thank you. A custodian from Aravi will reach out within 24 hours.');
      setForm({ name: '', email: '', phone: '', interest: INTEREST_OPTIONS[0], message: '' });
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={ENQUIRY.root}
      className="relative bg-[#F5F1E8] py-24 lg:py-32"
    >
      <div className="px-6 md:px-10 lg:px-16 max-w-[1500px] mx-auto">
        <div className="hairline w-full mb-16" />
        <div className="grid grid-cols-12 gap-10 lg:gap-16">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 lg:col-span-5"
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="w-10 h-px bg-[#A66B45]" />
              <span className="text-[11px] tracking-[0.4em] uppercase text-[#A66B45]">
                {eyebrow}
              </span>
            </div>
            <h2 className="font-serif text-[#18342A] text-4xl sm:text-5xl md:text-6xl lg:text-[64px] leading-[1.02] tracking-tight font-light">
              {heading || (<>Walk the land<br />with us.</>)}
            </h2>
            <p className="mt-8 text-[#6D6A61] text-base leading-relaxed font-light max-w-md">
              {intro}
            </p>

            <div className="mt-12 space-y-6">
              <Info icon={<Phone size={16} strokeWidth={1.4} />} label="Speak with us" value="+91 94919 29527" />
              <Info icon={<Phone size={16} strokeWidth={1.4} />} label="Alternate line" value="+91 94949 49527" />
              <Info icon={<Mail size={16} strokeWidth={1.4} />} label="Write to us" value="sales@araviecohomes.com" />
              <Info icon={<MapPin size={16} strokeWidth={1.4} />} label="Office" value="Cyber Residency, Gachibowli, Hyderabad" />
            </div>

            <div className="hairline w-full mt-12" />
            <div className="mt-6 flex items-center gap-3 text-[10px] tracking-[0.4em] uppercase text-[#6D6A61]">
              <span className="w-1 h-1 rounded-full bg-[#C9A458]" />
              HMDA Limits · Phase I bookings open
            </div>
          </motion.div>

          {/* Right - form */}
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 lg:col-span-7 lg:pl-10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
              <Field label="Your Name" required>
                <input
                  type="text"
                  data-testid={ENQUIRY.nameInput}
                  value={form.name}
                  onChange={update('name')}
                  className="w-full bg-transparent border-0 border-b border-[#18342A]/25 focus:border-[#C9A458] outline-none py-3 text-[#18342A] placeholder:text-[#6D6A61]/60 font-light"
                  placeholder="e.g. Anika Mehta"
                />
              </Field>
              <Field label="Email" required>
                <input
                  type="email"
                  data-testid={ENQUIRY.emailInput}
                  value={form.email}
                  onChange={update('email')}
                  className="w-full bg-transparent border-0 border-b border-[#18342A]/25 focus:border-[#C9A458] outline-none py-3 text-[#18342A] placeholder:text-[#6D6A61]/60 font-light"
                  placeholder="you@email.com"
                />
              </Field>
              <Field label="Phone">
                <input
                  type="tel"
                  data-testid={ENQUIRY.phoneInput}
                  value={form.phone}
                  onChange={update('phone')}
                  className="w-full bg-transparent border-0 border-b border-[#18342A]/25 focus:border-[#C9A458] outline-none py-3 text-[#18342A] placeholder:text-[#6D6A61]/60 font-light"
                  placeholder="+91 ·"
                />
              </Field>
              <Field label="Residence of Interest">
                <select
                  data-testid={ENQUIRY.interestSelect}
                  value={form.interest}
                  onChange={update('interest')}
                  className="w-full bg-transparent border-0 border-b border-[#18342A]/25 focus:border-[#C9A458] outline-none py-3 text-[#18342A] font-light appearance-none cursor-pointer"
                >
                  {INTEREST_OPTIONS.map((o) => (
                    <option key={o} value={o} className="bg-[#F5F1E8] text-[#18342A]">{o}</option>
                  ))}
                </select>
              </Field>
              <Field label="A few words" className="md:col-span-2">
                <textarea
                  rows={3}
                  data-testid={ENQUIRY.messageInput}
                  value={form.message}
                  onChange={update('message')}
                  className="w-full bg-transparent border-0 border-b border-[#18342A]/25 focus:border-[#C9A458] outline-none py-3 text-[#18342A] placeholder:text-[#6D6A61]/60 font-light resize-none"
                  placeholder="Tell us what you're looking for — a quiet ridge, a forest creek, a particular region..."
                />
              </Field>
            </div>

            <div className="mt-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#6D6A61] max-w-xs">
                By submitting, you agree to receive a single, considered reply from our team.
              </p>
              <button
                type="submit"
                data-testid={ENQUIRY.submit}
                disabled={submitting}
                className="group inline-flex items-center gap-3 bg-[#18342A] text-[#F5F1E8] px-9 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#C9A458] hover:text-[#18342A] transition-colors duration-500 disabled:opacity-60"
              >
                {submitting ? 'Sending…' : 'Send Enquiry'}
                <ArrowRight size={14} strokeWidth={1.6} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children, required, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="text-[10px] tracking-[0.4em] uppercase text-[#6D6A61] flex items-center gap-2">
      {label}
      {required && <span className="text-[#A66B45]">•</span>}
    </span>
    <div className="mt-1">{children}</div>
  </label>
);

const Info = ({ icon, label, value }) => (
  <div className="flex items-start gap-4">
    <div className="w-9 h-9 grid place-items-center border border-[#C9A458]/40 text-[#A66B45] mt-0.5">
      {icon}
    </div>
    <div>
      <div className="text-[10px] tracking-[0.4em] uppercase text-[#6D6A61]">{label}</div>
      <div className="font-serif text-[#18342A] text-xl mt-1 leading-tight">{value}</div>
    </div>
  </div>
);

export default Enquiry;
